import React, { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import ServiceRequestModal from "./ServiceRequestModal";
import SearchModal from "./SearchModal";
import EngineeringIcon from "./EngineeringIcons";

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // Add state for tracking which dropdown is open
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItemClasses = (path: string) => `
    text-white py-4 px-2 hover:text-gray-200
    font-['Montserrat'] text-xs font-medium
    flex items-center justify-between
    ${isActive(path) ? "border-b-2 border-white" : ""}
  `;

  const mobileNavItemClasses = (path: string) => `
    text-white py-2 px-4 hover:bg-orange-600
    font-['Montserrat'] text-xs font-medium
    flex items-center justify-between
    ${isActive(path) ? "bg-orange-600" : ""}
  `;

  const dropdownClasses = `
    absolute left-0 mt-0 bg-orange-500 min-w-[200px]
    shadow-lg rounded-b-lg overflow-hidden z-50
    transition-all duration-300 ease-in-out
    transform origin-top
  `;

  const dropdownItemClasses = `
    text-white py-3 px-4 hover:bg-orange-600
    font-['Montserrat'] text-xs font-medium
    block w-full text-left whitespace-nowrap
  `;

  return (
    <>
      <nav className="bg-white shadow-md">
        {/* Top Bar */}
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center py-2">
            {/* Logo */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <img
                  src="/logo_png.png"
                  alt="Ekumfi Engineering"
                  className="h-8"
                />
                <div className="ml-2">
                  <span className="text-xl font-bold text-gray-600">Ekumfi Engineering</span>
                  <span className="text-sm block text-gray-600">
                    Engineering Conglomerate
                  </span>
                </div>
              </div>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden text-gray-600"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>

            {/* Contact Info - Desktop */}
            <div className={`hidden lg:flex items-center space-x-6`}>
              <div className="flex items-center">
                <div className="text-orange-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-2">
                  <p className="text-sm text-gray-600">
                    Mon - Fri: 06:00AM - 18:00PM
                  </p>
                  <p className="text-sm text-gray-600">
                    Sat - Sun: 06:00AM - 05:00PM
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="text-orange-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-2">
                  <p className="text-sm text-gray-600">Atta-Mills Street,</p>
                  <p className="text-sm text-gray-600">
                    Ekumfi Essuehyia, Central Region, GH
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="text-orange-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                </div>
                <div className="ml-2">
                  <p className="text-sm text-gray-600">Call Us:</p>
                  <p className="font-semibold text-gray-600">024-086-5997</p>
                </div>
              </div>
              <button
                className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors"
                onClick={() => setIsServiceModalOpen(true)}
              >
                Explore Our Services
              </button>
            </div>

            {/* Mobile Contact Info */}
            <div
              className={`${
                isMenuOpen ? "flex" : "hidden"
              } lg:hidden flex-col space-y-4 mt-4 pb-4`}
            >
              <div className="flex items-center">
                <div className="text-orange-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-2">
                  <p className="text-sm text-gray-600">
                    Mon - Fri: 06:00AM - 18:00PM
                  </p>
                  <p className="text-sm text-gray-600">
                    Sat - Sun: 06:00AM - 05:00PM
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="text-orange-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-2">
                  <p className="text-sm text-gray-600">Atta-Mills Street,</p>
                  <p className="text-sm text-gray-600">
                    Ekumfi Essuehyia, Central Region, GH
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="text-orange-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                </div>
                <div className="ml-2">
                  <p className="text-sm text-gray-600">Call Us:</p>
                  <p className="font-semibold text-gray-600">024-086-5997</p>
                </div>
              </div>
              <button className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors w-full">
                EXPLORE SERVICES
              </button>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="bg-orange-500">
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center">
              {/* Desktop Menu */}
              <div className="hidden lg:flex space-x-8">
                <a href="/" className={navItemClasses("/")}>
                  HOME
                </a>
                <a href="/about" className={navItemClasses("/about")}>
                  ABOUT
                </a>
                <div className="relative">
                  <a
                    href="/services"
                    className={navItemClasses("/services")}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveDropdown(
                        activeDropdown === "services" ? null : "services"
                      );
                    }}
                  >
                    <span>SERVICES</span>
                    <svg
                      className={`w-4 h-4 ml-1 transform transition-transform ${
                        activeDropdown === "services" ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </a>
                  {activeDropdown === "services" && (
                    <div className={dropdownClasses}>
                      <a
                        href="/services/research-development"
                        className={dropdownItemClasses}
                      >
                        Research & Development
                      </a>
                      <a
                        href="/services/farm-machinery"
                        className={dropdownItemClasses}
                      >
                        Farm Machinery & Food Processing
                      </a>
                      <a
                        href="/services/energy-engineering"
                        className={dropdownItemClasses}
                      >
                        Energy Engineering
                      </a>
                      <a
                        href="/services/marine-engineering"
                        className={dropdownItemClasses}
                      >
                        Boats & Yachts Building
                      </a>
                      <a
                        href="/services/wood-engineering"
                        className={dropdownItemClasses}
                      >
                        Wood Works Engineering
                      </a>
                      <a
                        href="/services/plumbing-engineering"
                        className={dropdownItemClasses}
                      >
                        Plumbing Engineering
                      </a>
                      <a
                        href="/services/sustainable-building"
                        className={dropdownItemClasses}
                      >
                        Sustainable Building
                      </a>
                      <a
                        href="/services/tvet-training"
                        className={dropdownItemClasses}
                      >
                        TVET/Skills Training
                      </a>
                      <a
                        href="/services/software-ml"
                        className={dropdownItemClasses}
                      >
                        Software & Machine Learning
                      </a>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <a
                    href="/projects"
                    className={navItemClasses("/projects")}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveDropdown(
                        activeDropdown === "projects" ? null : "projects"
                      );
                    }}
                  >
                    <span>PROJECTS</span>
                    <svg
                      className={`w-4 h-4 ml-1 transform transition-transform ${
                        activeDropdown === "projects" ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </a>
                  {activeDropdown === "projects" && (
                    <div className={dropdownClasses}>
                      <a
                        href="/projects/completed"
                        className={dropdownItemClasses}
                      >
                        Completed Projects
                      </a>
                      <a
                        href="/projects/ongoing"
                        className={dropdownItemClasses}
                      >
                        Ongoing Projects
                      </a>
                      {/* <a
                        href="/projects/gallery"
                        className={dropdownItemClasses}
                      >
                        Project Gallery
                      </a> */}
                    </div>
                  )}
                </div>
                <div className="relative">
                  <a
                    href="/training"
                    className={navItemClasses("/training")}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveDropdown(
                        activeDropdown === "training" ? null : "training"
                      );
                    }}
                  >
                    <span>TRAININGS</span>
                    <svg
                      className={`w-4 h-4 ml-1 transform transition-transform ${
                        activeDropdown === "training" ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </a>
                  {activeDropdown === "training" && (
                    <div className={dropdownClasses}>
                      <a
                        href="/training/new-trainings"
                        className={dropdownItemClasses}
                      >
                        Join Our Trainings
                      </a>
                      {/* <a
                        href="/training/ongoing"
                        className={dropdownItemClasses}
                      >
                        Ongoing Trainings
                      </a> */}
                    </div>
                  )}
                </div>
                <div className="relative group">
                  <a href="/team" className={navItemClasses("/team")}>
                    TEAM
                  </a>
                </div>
                <a href="/contacts" className={navItemClasses("/contacts")}>
                  CONTACTS
                </a>
              </div>

              {/* Mobile Menu */}
              <div
                className={`${
                  isMenuOpen ? "block" : "hidden"
                } lg:hidden absolute top-full left-0 right-0 bg-orange-500 z-50`}
              >
                <div className="flex flex-col py-2">
                  <a href="/" className={mobileNavItemClasses("/")}>
                    HOME
                  </a>
                  <a href="/about" className={mobileNavItemClasses("/about")}>
                    ABOUT
                  </a>
                  <div className="relative">
                    <a
                      href="/services"
                      className={mobileNavItemClasses("/services")}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveDropdown(
                          activeDropdown === "mobile-services"
                            ? null
                            : "mobile-services"
                        );
                      }}
                    >
                      <span>SERVICES</span>
                      <svg
                        className={`w-4 h-4 ml-1 transform transition-transform ${
                          activeDropdown === "mobile-services"
                            ? "rotate-180"
                            : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </a>
                    <div
                      className={`bg-orange-600 ${
                        activeDropdown === "mobile-services"
                          ? "block"
                          : "hidden"
                      }`}
                    >
                      <a
                        href="/services/research-development"
                        className={`${dropdownItemClasses} pl-8`}
                      >
                        Research & Development
                      </a>
                      <a
                        href="/services/farm-machinery"
                        className={`${dropdownItemClasses} pl-8`}
                      >
                        Farm Machinery & Food Processing
                      </a>
                      <a
                        href="/services/energy-engineering"
                        className={`${dropdownItemClasses} pl-8`}
                      >
                        Energy Engineering
                      </a>
                      <a
                        href="/services/marine-engineering"
                        className={`${dropdownItemClasses} pl-8`}
                      >
                        Boats & Yachts Building
                      </a>
                      <a
                        href="/services/wood-engineering"
                        className={`${dropdownItemClasses} pl-8`}
                      >
                        Wood Works Engineering
                      </a>
                      <a
                        href="/services/plumbing-engineering"
                        className={`${dropdownItemClasses} pl-8`}
                      >
                        Plumbing Engineering
                      </a>
                      <a
                        href="/services/sustainable-building"
                        className={`${dropdownItemClasses} pl-8`}
                      >
                        Sustainable Building
                      </a>
                      <a
                        href="/services/tvet-training"
                        className={`${dropdownItemClasses} pl-8`}
                      >
                        TVET/Skills Training
                      </a>
                      <a
                        href="/services/software-ml"
                        className={`${dropdownItemClasses} pl-8`}
                      >
                        Software & Machine Learning
                      </a>
                    </div>
                  </div>
                  <div className="relative">
                    <a
                      href="/projects"
                      className={mobileNavItemClasses("/projects")}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveDropdown(
                          activeDropdown === "mobile-projects"
                            ? null
                            : "mobile-projects"
                        );
                      }}
                    >
                      <span>PROJECTS</span>
                      <svg
                        className={`w-4 h-4 ml-1 transform transition-transform ${
                          activeDropdown === "mobile-projects"
                            ? "rotate-180"
                            : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </a>
                    <div
                      className={`bg-orange-600 ${
                        activeDropdown === "mobile-projects"
                          ? "block"
                          : "hidden"
                      }`}
                    >
                      <a
                        href="/projects/completed"
                        className={`${dropdownItemClasses} pl-8`}
                      >
                        Completed Projects
                      </a>
                      <a
                        href="/projects/ongoing"
                        className={`${dropdownItemClasses} pl-8`}
                      >
                        Ongoing Projects
                      </a>
                      <a
                        href="/projects/gallery"
                        className={`${dropdownItemClasses} pl-8`}
                      >
                        Project Gallery
                      </a>
                    </div>
                  </div>
                  <div className="relative group">
                    <a href="/team" className={mobileNavItemClasses("/team")}>
                      TEAM
                      <svg
                        className="w-4 h-4 ml-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </a>
                  </div>
                  <a href="/blog" className={mobileNavItemClasses("/blog")}>
                    BLOG
                  </a>
                  <a
                    href="/contacts"
                    className={mobileNavItemClasses("/contacts")}
                  >
                    CONTACTS
                  </a>
                  <button
                    onClick={() => setIsSearchModalOpen(true)}
                    className="text-white py-2 px-4 hover:bg-orange-600 font-['Montserrat'] text-xs font-medium flex items-center gap-2"
                  >
                    <EngineeringIcon type="wrench" size="sm" className="text-white" animated={false} />
                    SEARCH
                  </button>
                </div>
              </div>

              {/* Search Button */}
              <button 
                className="text-white p-4 hover:text-orange-200 transition-colors flex items-center gap-2"
                onClick={() => setIsSearchModalOpen(true)}
              >
                <EngineeringIcon type="wrench" size="sm" className="text-white" animated={false} />
                <span className="hidden sm:inline text-sm font-medium">Search</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <ServiceRequestModal
        isOpen={isServiceModalOpen}
        onClose={() => setIsServiceModalOpen(false)}
      />
      
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />
    </>
  );
};

export default Navbar;
